import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import menuMeatballTokens from '@amzn/meridian-tokens/base/icon/menu-meatball';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router';

import DataSource from '../../../api';
import { StateContext } from '../../../GlobalStateStore';
import { ExtendedUser } from '../../../Types';
import DataState from '../../../Types/DataState';
import { localizeErrorCode, ErrorCode } from '../../../Types/ErrorCode';
import {
  AddCoachResponse,
  EMPTY_ADD_COACH_RESPONSE,
} from '../../../Types/AddCoachResponse';
import { getCurrentWeek, renderUrl, urls } from '../../../Util';
import useCustomTracking from '../../../Util/useCustomTracking';
import UserBadge from './UserBadge';
import useIsAdminOrImpersonating from '../../../hooks/useIsAdminOrImpersonating';
import useProxy from '../../../hooks/useProxy';
import useSyncedState from '../../../hooks/useSyncedState';
import PerformancePortalContext from '../../PerformancePortalContext';
import Naws, { QueryVariables } from '../../../api/Naws';

interface CoachesProps {
  coaches: ExtendedUser[];
}

const maxDisplayedProfiles = 10;
const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');

const Coaches: React.FC<CoachesProps> = ({
  coaches,
}: CoachesProps): React.ReactElement => {
  const intl = useIntl();
  let { userId } = useParams() as { userId: string };
  let defaultErrorMessage = 'Something went wrong!';
  const globalState = useContext(StateContext);
  const { refetchProfiles } = useContext(PerformancePortalContext);
  const {
    state: { inspireOptions },
  } = globalState;
  const { userID, cscEntityPrefix, requesterOwner } = inspireOptions.data;
  userId = userID;
  const [profiles, setProfiles] = useSyncedState<ExtendedUser[]>(coaches);
  const [removeState, setRemoveState] = useState(DataState.LOADED);
  const [addState, setAddState] = useState(DataState.INITIALIZED);
  const [addResponse, setAddResponse] = useState<AddCoachResponse>(
    EMPTY_ADD_COACH_RESPONSE
  );

  const isAdminOrImpersonating = useIsAdminOrImpersonating();
  const { isProxying } = useProxy();

  const [addErrorResponseMessage, setAddErrorResponseMessage] =
    useState(defaultErrorMessage);

  const [coachUsername, setCoachUsername] = useState('');

  const [open, setOpen] = useState(false);
  const onClickMoreButton = useCallback(() => setOpen(true), []);
  const onMoreClose = useCallback(() => setOpen(false), []);

  const [openAdd, setOpenAdd] = useState(false);
  const onClickAddButton = useCallback(() => setOpenAdd(true), []);
  const [ModalCloseTrackingTag, modalClosingTrackClick] = useCustomTracking(
    'dashboard-coaches-profile-sharing-modal-close',
    'dashboard-coaches-profile-sharing-modal-close',
    {
      'data-csa-c-type': 'modal',
    }
  );
  const [ModalOpenTrackingTag, modalOpenTrackClick] = useCustomTracking(
    'dashboard-coaches-profile-sharing-modal-open',
    'dashboard-coaches-profile-sharing-modal-open',
    {
      'data-csa-c-type': 'modal',
    }
  );
  const onAddClose = useCallback(() => {
    modalClosingTrackClick();
    setOpenAdd(false);
  }, []);

  const onAddOpen = useCallback(() => {
    modalOpenTrackClick();
  }, []);

  const onRemoveProfile = (profileId: string): void => {
    if (!userId) {
      return;
    }
    const removeCoachInput = apiNaws.prepareQueryForRemoveCoach(
      userId,
      profileId,
      cscEntityPrefix
    );
    apiNaws.removeCoach(
      removeCoachInput,
      setRemoveState,
      profiles,
      setProfiles,
      refetchProfiles
    );
  };

  const onAddProfile = (): void => {
    if (!userId) {
      return;
    }
    const addCoachInput = apiNaws.prepareQueryForAddCoach(
      userId,
      coachUsername,
      requesterOwner,
      cscEntityPrefix
    );

    const getProfileInput = apiNaws.prepareQuery(
      getCurrentWeek(),
      userId,
      cscEntityPrefix
    ) as QueryVariables;

    apiNaws.addCoach(
      addCoachInput,
      setAddState,
      setAddResponse,
      getProfileInput,
      (updatedProfiles: Array<any>) => {
        setProfiles(updatedProfiles);
      },
      refetchProfiles
    );
  };

  const localizeErrorMessage = (messageCode: string, alias: string): string => {
    return intl.formatMessage(localizeErrorCode(messageCode as ErrorCode), {
      alias: alias ? alias : '',
    });
  };

  useEffect(() => {
    if (addState === DataState.LOADED) {
      const { messageCode } = addResponse;
      if (messageCode && messageCode !== 'SUCCESS') {
        setAddState(DataState.ERROR);
        const localizedMessage = localizeErrorMessage(
          messageCode,
          coachUsername
        );
        setAddErrorResponseMessage(localizedMessage);
      } else {
        setOpenAdd(false);
      }
    }
  }, [addState]);

  const profileCount: number = profiles.length;
  const profileBadges = (
    profileCount <= maxDisplayedProfiles
      ? profiles
      : profiles.slice(0, maxDisplayedProfiles - 1)
  ).map((profile: ExtendedUser) => {
    return (
      <Box key={profile.userName}>
        <Row spacing='none' spacingInset='200 300 200 none'>
          <Box width='34.9px' height='36px'>
            <UserBadge
              username={profile.userName}
              givenName={profile.displayName}
              tooltip
              link={
                isAdminOrImpersonating
                  ? renderUrl(`${urls.dashboard}/${profile.userID}`)
                  : undefined
              }
            />
          </Box>
        </Row>
      </Box>
    );
  });

  return (
    <Box>
      <Column spacing='300'>
        <Row>
          <Text type='h100'>
            <FormattedMessage
              id='cs_perfportal_myCoaches'
              defaultMessage='My Coaches'
            />{' '}
            ({profileCount})
          </Text>
          {!isProxying && (
            <Text type='h100'>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                onClick={onClickAddButton}
                type='secondary'
                data-csa-c-type='link'
                data-csa-c-slot-id='share-profile-link-slot-id'
                data-csa-c-content-id='share-profile-link'
                data-csa-c-description='Share profile with another user'
                data-csa-c-tag1='Dashboard'
              >
                <Icon tokens={exportSmallTokens} />{' '}
                <FormattedMessage
                  id='cs_perfportal_share_button'
                  defaultMessage='Share'
                  description='Share button'
                />
              </Link>
              <ModalCloseTrackingTag />
              <ModalOpenTrackingTag />
              <Modal
                title={intl.formatMessage({
                  id: 'cs_perfportal_ShareYourProfile',
                  defaultMessage: 'Share your profile',
                })}
                open={openAdd}
                onClose={onAddClose}
                onOpen={onAddOpen}
                scrollContainer='viewport'
                closeLabel={intl.formatMessage({
                  id: 'cs_perfportal_popover_close',
                  defaultMessage: 'close',
                })}
                aria-describedby='modal-description'
                width={300}
              >
                {addState === DataState.ERROR && (
                  <Alert type='error' width='100%'>
                    {addErrorResponseMessage}
                  </Alert>
                )}
                <div
                  id='dashboad-coaches-modal-closing'
                  data-csa-c-type='element'
                  data-csa-c-slot-id='dashboad-coaches-modal-closing'
                  data-csa-c-content-id='dashboad-coaches-modal-closing'
                />
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_enterAUserLogin'
                    defaultMessage='Enter a valid user login'
                  />
                </Text>
                <Input
                  data-csa-c-type='element'
                  data-csa-c-slot-id='dashboard-coaches-input-field-slot'
                  data-csa-c-content-id='dashboard-coaches-input-field'
                  value={coachUsername}
                  onChange={setCoachUsername}
                  type='text'
                />
                <ModalFooter>
                  <Row alignmentHorizontal='end' widths='fit'>
                    <Button
                      data-csa-c-type='button'
                      data-csa-c-slot-id='dashboard-coaches-modal-button-slot'
                      data-csa-c-content-id='dashboard-coaches-modal-cancel-button'
                      type='secondary'
                      size='small'
                      onClick={onAddClose}
                    >
                      <FormattedMessage
                        id='cs_perfportal_cancel'
                        defaultMessage='Cancel'
                      />
                    </Button>
                    <Button
                      data-csa-c-type='button'
                      data-csa-c-slot-id='dashboard-coaches-modal-button-slot'
                      data-csa-c-content-id='dashboard-coaches-modal-share-button'
                      type='primary'
                      size='small'
                      onClick={onAddProfile}
                      disabled={coachUsername.trim() === ''}
                    >
                      <FormattedMessage
                        id='cs_perfportal_share_button'
                        defaultMessage='Share'
                        description='Share button'
                      />
                    </Button>
                  </Row>
                </ModalFooter>
              </Modal>
            </Text>
          )}
        </Row>
        <Row
          width='100%'
          widths='44px'
          alignmentHorizontal='start'
          wrap='down'
          spacing='none'
        >
          {profileCount > 0 ? (
            <>
              {profileBadges}
              <Button
                data-csa-c-type='button'
                data-csa-c-slot-id='dashboard-coaches-profile-button-slot'
                data-csa-c-content-id='dashboard-coaches-profile-more-button'
                type='secondary'
                onClick={onClickMoreButton}
              >
                <Icon tokens={menuMeatballTokens} />
              </Button>

              <Modal
                title={intl.formatMessage({
                  id: 'cs_perfportal_myCoaches',
                  defaultMessage: 'My Coaches',
                })}
                open={open}
                onClose={onMoreClose}
                scrollContainer='viewport'
                closeLabel={intl.formatMessage({
                  id: 'cs_perfportal_popover_close',
                  defaultMessage: 'close',
                })}
                aria-describedby='modal-description'
                width={300}
              >
                {removeState === DataState.ERROR && (
                  <Alert type='error'>
                    <FormattedMessage
                      id='cs_perfportal_somethingWentWrong'
                      defaultMessage={'Something went wrong!'}
                    />
                  </Alert>
                )}
                {profiles.map((profile: ExtendedUser) => {
                  return (
                    <Box key={profile.userName}>
                      <Row
                        spacing='none'
                        widths={['grid-2', 'grid-8', 'grid-2']}
                        spacingInset='200 300 200 none'
                      >
                        <Box height='36px' width='36px'>
                          <UserBadge
                            username={profile.userName}
                            givenName={profile.displayName}
                          />
                        </Box>
                        <Box>
                          <Text>{profile.displayName}</Text>
                          <Text>{profile.userName}</Text>
                        </Box>

                        <Box>
                          {profile.removable && (
                            <Button
                              data-csa-c-type='button'
                              data-csa-c-slot-id='dashboard-coaches-profile-button-slot'
                              data-csa-c-content-id='dashboard-coaches-profile-remove-button'
                              data-csa-c-profile-id={`${profile.userID}`}
                              type='secondary'
                              onClick={(): void => {
                                onRemoveProfile(profile.userID);
                              }}
                            >
                              <Icon tokens={closeTokens} />
                            </Button>
                          )}
                        </Box>
                      </Row>
                    </Box>
                  );
                })}
              </Modal>
            </>
          ) : (
            <FormattedMessage
              id='cs_perfportal_no_coaches'
              defaultMessage="You don't have any coaches"
              description='header of PerfMessages Table'
            />
          )}
        </Row>
      </Column>
    </Box>
  );
};

export default Coaches;
